/**
 * Credit / debit notes issued against an invoice.
 *
 * A note never changes the invoice total; it changes what is owed on it. The backend derives
 * balance_due = invoice total − applied payments − credit notes + debit notes, so a credit note
 * lowers the balance and a debit note raises it.
 */

export const NOTE_TYPE_CREDIT = 'CREDIT';
export const NOTE_TYPE_DEBIT = 'DEBIT';

const toAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const isCreditNote = (note) => String(note?.note_type || '').toUpperCase() === NOTE_TYPE_CREDIT;
export const isDebitNote = (note) => String(note?.note_type || '').toUpperCase() === NOTE_TYPE_DEBIT;

/** Readable label for the type column: "Credit note" rather than the raw CREDIT. */
export const noteTypeLabel = (note) => {
  if (isCreditNote(note)) return 'Credit note';
  if (isDebitNote(note)) return 'Debit note';
  return note?.note_type || '';
};

/** Signed effect on the balance due: negative for a credit note, positive for a debit note. */
export const noteBalanceEffect = (note) => {
  const amount = Math.abs(toAmount(note?.amount));
  if (isCreditNote(note)) return -amount;
  if (isDebitNote(note)) return amount;
  return 0;
};

/** Totals for the invoice summary, with the note numbers that make them up. */
export const summariseAdjustmentNotes = (notes) => {
  const list = Array.isArray(notes) ? notes : [];
  const credits = list.filter(isCreditNote);
  const debits = list.filter(isDebitNote);
  const creditTotal = credits.reduce((sum, n) => sum + Math.abs(toAmount(n.amount)), 0);
  const debitTotal = debits.reduce((sum, n) => sum + Math.abs(toAmount(n.amount)), 0);
  return {
    creditTotal,
    debitTotal,
    net: debitTotal - creditTotal,
    creditNumbers: credits.map((n) => n.note_number).filter(Boolean),
    debitNumbers: debits.map((n) => n.note_number).filter(Boolean),
  };
};
